import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import Header from 'components/header/Header.container';
import Footer from 'components/footer/Footer';
import Loader from 'components/loader/loader';
import InputField from 'components/textField/InputField';
import CompactMobileSearch from 'components/compactMobileSearch/CompactMobileSearch';
import { useCompactMobileSearch } from 'hooks/useCompactMobileSearch';
import searchIcon from 'assets/svg/search.svg';
import newsPlaceholder from 'assets/svg/news-placeholder-image.svg';
import newsSourceLogo from 'assets/svg/news-source-logo.svg';
import { NewsItem, NEWS_PER_PAGE, NEWS_LOAD_BATCH_TOUCH } from 'views/news/newsTypes';
import { fetchNewsBatch } from 'views/news/fetchNews';
import { useNewsRealtime } from 'views/news/useNewsRealtime';
import Styles from './NewsPage.module.scss';

const DESKTOP_QUERY = '(min-width: 1201px)';
const MOBILE_QUERY = '(max-width: 767px)';
const SCROLL_THRESHOLD_PX = 100;
/** When content fits the viewport, still allow prefetch without scrolling. */
const PAGE_FILL_BUFFER_PX = 50;

const filterNews = (items: NewsItem[], query: string): NewsItem[] => {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter(item => item.description.toLowerCase().includes(q));
};

const NewsCard: FC<{ item: NewsItem; featured?: boolean; onOpen?: () => void }> = ({
  item,
  featured,
  onOpen,
}) => (
  <div
    className={`${Styles.card} ${featured ? Styles.featured : ''} ${item.isRead ? '' : Styles.unread}`}
    onClick={onOpen}
    onKeyDown={e => {
      if (onOpen && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        onOpen();
      }
    }}
    role={onOpen ? 'button' : undefined}
    tabIndex={onOpen ? 0 : undefined}
  >
    <div className={Styles.cardImage}>
      <img src={item.image || newsPlaceholder} alt="" />
    </div>
    <div className={Styles.cardBody}>
      <div className={Styles.cardMeta}>
        <img className={Styles.metaThumb} src={item.thumbnail || newsSourceLogo} alt="" />
        <span className={Styles.label}>{item.label}</span>
      </div>
      <p className={Styles.description}>{item.description}</p>
      <span className={Styles.readMore}>Read More</span>
    </div>
  </div>
);

const NewsPage: FC = () => {
  const history = useHistory();
  const [page, setPage] = useState(0);
  const [direction, setDirection] = useState<'next' | 'prev'>('next');
  const [searchQuery, setSearchQuery] = useState('');
  const [allNews, setAllNews] = useState<NewsItem[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia(DESKTOP_QUERY).matches);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);

  // Touch (mobile/tablet) infinite-scroll list.
  const [touchNews, setTouchNews] = useState<NewsItem[]>([]);
  const [touchHasMore, setTouchHasMore] = useState(false);
  const [touchInitialLoading, setTouchInitialLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const prefetchSentinelRef = useRef<HTMLDivElement>(null);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY);
    const handle = () => setIsDesktop(mq.matches);
    handle();
    mq.addEventListener('change', handle);
    return () => mq.removeEventListener('change', handle);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const handle = () => setIsMobile(mq.matches);
    handle();
    mq.addEventListener('change', handle);
    return () => mq.removeEventListener('change', handle);
  }, []);

  // Real-time: a published/updated news item bumps refreshKey (no toast),
  // which re-runs the loaders below.
  useNewsRealtime(() => setRefreshKey((k) => k + 1));

  // Desktop loads the full list (client-side paginated); touch loads the first
  // batch and grows via infinite scroll.
  useEffect(() => {
    if (isDesktop) {
      setPage(0);
      let cancelled = false;
      fetchNewsBatch(0, 500)
        .then(({ news }) => {
          if (!cancelled) setAllNews(news);
        })
        .catch(() => {});
      return () => {
        cancelled = true;
      };
    }

    let cancelled = false;
    const loadInitial = async () => {
      setTouchInitialLoading(true);
      setTouchNews([]);
      try {
        const { news, hasMore } = await fetchNewsBatch(0, NEWS_LOAD_BATCH_TOUCH);
        if (!cancelled) {
          setTouchNews(news);
          setTouchHasMore(hasMore);
        }
      } finally {
        if (!cancelled) setTouchInitialLoading(false);
      }
    };
    loadInitial();
    return () => {
      cancelled = true;
    };
  }, [isDesktop, refreshKey]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const isSearchActive = searchQuery.trim().length > 0;
  const {
    searchInputRef,
    showCompactSearch,
    isSearchExpanded,
    openMobileSearch,
    handleMobileSearchBlur,
  } = useCompactMobileSearch({ isMobile, isSearchActive });

  const filtered = useMemo(() => filterNews(allNews, searchQuery), [allNews, searchQuery]);
  const effectiveTouchHasMore = !isSearchActive && touchHasMore;

  const loadMoreNews = useCallback(async () => {
    if (isDesktop || isSearchActive || loadingMoreRef.current || !touchHasMore) return;

    loadingMoreRef.current = true;
    setIsLoadingMore(true);
    try {
      const { news, hasMore } = await fetchNewsBatch(touchNews.length, NEWS_LOAD_BATCH_TOUCH);
      if (news.length > 0) {
        setTouchNews(prev => [...prev, ...news]);
      }
      setTouchHasMore(hasMore);
    } finally {
      loadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, [isDesktop, isSearchActive, touchNews.length, touchHasMore]);

  const checkPrefetchLoad = useCallback(() => {
    if (
      isDesktop ||
      isSearchActive ||
      loadingMoreRef.current ||
      !touchHasMore ||
      touchInitialLoading ||
      isLoadingMore
    ) {
      return;
    }

    const sentinel = prefetchSentinelRef.current;
    if (!sentinel) return;

    const rect = sentinel.getBoundingClientRect();
    const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
    if (!isVisible) return;

    const userHasScrolled = window.scrollY >= SCROLL_THRESHOLD_PX;
    const pageFillsViewport =
      document.documentElement.scrollHeight <= window.innerHeight + PAGE_FILL_BUFFER_PX;

    if (userHasScrolled || pageFillsViewport) {
      loadMoreNews();
    }
  }, [isDesktop, isSearchActive, touchHasMore, touchInitialLoading, isLoadingMore, loadMoreNews]);

  useEffect(() => {
    if (isDesktop || touchInitialLoading || !effectiveTouchHasMore || isLoadingMore) {
      return undefined;
    }
    const frameId = requestAnimationFrame(() => checkPrefetchLoad());
    return () => cancelAnimationFrame(frameId);
  }, [isDesktop, touchInitialLoading, effectiveTouchHasMore, isLoadingMore, touchNews.length, checkPrefetchLoad]);

  useEffect(() => {
    if (isDesktop || !effectiveTouchHasMore || touchInitialLoading || isLoadingMore) {
      return undefined;
    }
    const sentinel = prefetchSentinelRef.current;
    if (!sentinel) return undefined;

    const observer = new IntersectionObserver(() => checkPrefetchLoad(), {
      rootMargin: '0px',
      threshold: 0.25,
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isDesktop, effectiveTouchHasMore, touchInitialLoading, isLoadingMore, checkPrefetchLoad, touchNews.length]);

  useEffect(() => {
    if (isDesktop || !effectiveTouchHasMore || touchInitialLoading) return undefined;
    window.addEventListener('scroll', checkPrefetchLoad, { passive: true });
    return () => window.removeEventListener('scroll', checkPrefetchLoad);
  }, [isDesktop, effectiveTouchHasMore, touchInitialLoading, checkPrefetchLoad]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / NEWS_PER_PAGE));

  // Desktop: current page → 2 featured + grid.
  const pageItems = filtered.slice(page * NEWS_PER_PAGE, page * NEWS_PER_PAGE + NEWS_PER_PAGE);
  const featured = pageItems.slice(0, 2);
  const rest = pageItems.slice(2);

  // Touch: the loaded list (search filters what's loaded).
  const touchVisible = isSearchActive ? filterNews(touchNews, searchQuery) : touchNews;

  const handleSearchChange = (val: string) => {
    setSearchQuery(val);
    setPage(0);
  };

  return (
    <div className="pageShell">
      <Header />
      <div className="pageShellMain">
        <div className="custom-container">
          <div className={Styles.page}>
            <h1 className={Styles.pageTitle}>News Aggregator</h1>

            {isMobile ? (
              <CompactMobileSearch
                value={searchQuery}
                onChange={handleSearchChange}
                onBlur={() => handleMobileSearchBlur(searchQuery)}
                placeholder="Search News"
                searchIcon={searchIcon}
                inputClassName={Styles.searchField}
                showCompactSearch={showCompactSearch}
                isSearchExpanded={isSearchExpanded}
                searchInputRef={searchInputRef}
                onOpenMobileSearch={openMobileSearch}
                openSearchAriaLabel="Open search"
                focusSearchAriaLabel="Search news"
              />
            ) : (
              <div className={`${Styles.searchBox} form-group`}>
                <InputField
                  placeholder="Search News"
                  type="text"
                  value={searchQuery}
                  cssClass={Styles.searchField}
                  icon={searchIcon}
                  iconClass={Styles.searchIcon}
                  handleChange={handleSearchChange}
                />
              </div>
            )}

            {isDesktop ? (
              /* ── Desktop: featured + grid + Previous/Next ── */
              <>
                {pageItems.length === 0 ? (
                  <p className={Styles.emptySearch}>No news found.</p>
                ) : (
                  <div
                    key={page}
                    className={`${Styles.pageCards} ${
                      direction === 'next' ? Styles.pageCardsNext : Styles.pageCardsPrev
                    }`}
                  >
                    {featured.length > 0 && (
                      <div className={Styles.featuredGrid}>
                        {featured.map(item => (
                          <NewsCard
                            key={item.id}
                            item={item}
                            featured
                            onOpen={() => history.push(`/news/${item.id}`)}
                          />
                        ))}
                      </div>
                    )}
                    {rest.length > 0 && (
                      <div className={Styles.cardsGrid}>
                        {rest.map(item => (
                          <NewsCard
                            key={item.id}
                            item={item}
                            onOpen={() => history.push(`/news/${item.id}`)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className={Styles.navRow}>
                  <button
                    type="button"
                    className={Styles.navBtn}
                    onClick={() => {
                      setDirection('prev');
                      setPage(p => Math.max(0, p - 1));
                    }}
                    disabled={page === 0}
                    aria-label="Previous news"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className={Styles.navBtn}
                    onClick={() => {
                      setDirection('next');
                      setPage(p => Math.min(totalPages - 1, p + 1));
                    }}
                    disabled={page >= totalPages - 1 || filtered.length === 0}
                    aria-label="Next news"
                  >
                    Next
                  </button>
                </div>
              </>
            ) : (
              /* ── Mobile/tablet: infinite scroll (no pagination buttons) ── */
              <>
                {touchInitialLoading && (
                  <div className={Styles.loadMoreBlock}>
                    <Loader />
                    <p className={Styles.loadStatus}>Loading News…</p>
                  </div>
                )}

                {!touchInitialLoading && touchVisible.length === 0 && (
                  <p className={Styles.emptySearch}>No News found.</p>
                )}

                {touchVisible.length > 0 && (
                  <div className={Styles.cardsGrid}>
                    {touchVisible.map(item => (
                      <NewsCard
                        key={item.id}
                        item={item}
                        onOpen={() => history.push(`/news/${item.id}`)}
                      />
                    ))}
                  </div>
                )}

                {effectiveTouchHasMore && (
                  <div ref={prefetchSentinelRef} className={Styles.loadSentinel} />
                )}

                {isLoadingMore && (
                  <div className={Styles.loadMoreBlock}>
                    <Loader />
                    <p className={Styles.loadStatus}>Loading more…</p>
                  </div>
                )}

                {!effectiveTouchHasMore &&
                  !touchInitialLoading &&
                  !isSearchActive &&
                  touchNews.length > NEWS_LOAD_BATCH_TOUCH && (
                    <p className={Styles.loadEnd}>All News loaded</p>
                  )}
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default NewsPage;
